import HoverArrowRightHovered from '@img/icons/hover-arrow-right-hovered.svg?url';
import HoverArrowRight from '@img/icons/hover-arrow-right.svg?url';
import TickCircle from '@img/icons/tick-circle.svg?url';
import { ButtonStyles } from '@stories/Components/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import { css } from 'styled-components';
import { transition } from './animate';
import brand from './brand';
import { fluid } from './fluid';
import fonts from './fonts';
import { Device, from, until } from './media';
export const headingStyles = css `
  ${fonts.agBook.bold};
  color: currentColor;
  margin: 0 0 ${fluid(24, 32)};
`;
export const h1 = css `
  ${headingStyles};

  ${fonts.agBook.bold};
  font-size: ${fluid(30, 48)};
  line-height: ${fluid(36, 52)};
`;
export const h2 = css `
  ${headingStyles};

  ${fonts.agBook.bold};
  font-size: ${fluid(22, 32)};
  line-height: ${fluid(30, 40)};
`;
export const h3 = css `
  ${headingStyles};

  font-size: ${fluid(22, 26)};
  line-height: ${fluid(28, 32)};
`;
export const h4 = css `
  ${headingStyles};

  font-size: ${fluid(20, 24)};
  line-height: ${fluid(26, 30)};
`;
export const h5 = css `
  ${headingStyles};

  font-size: ${fluid(18, 22)};
  line-height: ${fluid(24, 28)};
`;
export const h6 = css `
  ${headingStyles};

  font-size: ${fluid(16, 20)};
  line-height: ${fluid(22, 26)};
`;
const paragraphStyles = css `
  ${fonts.agBook.light};
  color: currentColor;
  margin: 0 0 ${fluid(16, 24)};
`;
export const paragraphLarge = css `
  ${paragraphStyles};

  ${fonts.agBook.bold};
  font-size: ${fluid(18, 24)};
  line-height: ${fluid(24, 30)};
`;
export const paragraph = css `
  ${paragraphStyles};

  font-size: ${fluid(18, 22)};
  line-height: ${fluid(24, 30)};
`;
export const paragraphSmallMed = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 20)};
  line-height: ${fluid(24, 26)};
`;
export const paragraphSmall = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 18)};
  line-height: ${fluid(24, 24)};
`;
export const paragraphTiny = css `
  ${paragraphStyles};

  font-size: ${fluid(16, 16)};
  line-height: ${fluid(24, 24)};
`;
export const caption = css `
  ${fonts.agBook.regular};
  text-transform: uppercase;
  font-size: 14px;
  line-height: 30px;
`;
export const label = css `
  ${paragraph};
`;
export const link = css `
  ${transition('text-decoration-color')};

  display: inline;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    text-decoration-color: transparent;
  }
`;
/** TODO: update styling for rich text component */
export const blockquote = css `
  position: relative;
  ${fonts.agBook.bold}
  font-size: ${fluid(20, 26)};
  line-height: ${fluid(26, 32)};
  margin: 2em 0;
  color: currentColor;
  padding: ${fluid(30, 40)} ${fluid(22, 43)} ${fluid(30, 40)} ${fluid(21, 36)};
  text-align: left;
  box-shadow: 0 0 6px ${rgba(brand.black, 0.16)};

  &::before,
  &::after {
    display: block;
    position: absolute;
    font-size: 124px;
    line-height: 62px;
    color: var(--themeColor);

    body:not([data-page-theme]) {
      --themeColor: ${brand.orange};
    }
  }

  &::before {
    content: '\\201C';
    top: -17px;
    left: -15px;
  }

  &::after {
    content: '\\201D';
    bottom: -60px;
    right: -15px;
  }

  > span,
  > cite {
    ${paragraph};
    position: relative;
    display: block;
    padding-left: 26px;
    margin: 22px 0 0;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: ${fluid(9, 12)};
      width: 16px;
      height: 0;
      border-top: 1px solid currentColor;
    }
  }
`;
export const blockquoteName = css `
  ${paragraphSmall};
  text-align: left;
  display: block;
`;
/** TODO: update styling for rich text component */
export const table = css `
  display: block;
  overflow-x: auto;
  padding-top: 1em;
  margin-bottom: 1em;
  width: 100%;

  @media ${until(Device.Tablet)} {
    white-space: nowrap;
  }

  table,
  td,
  th {
    border: 1px solid ${brand.black};
  }

  tbody {
    display: table;
    width: 100%;
  }

  thead {
    tr {
      td,
      th {
        ${fonts.agBook.bold};
        font-size: ${fluid(20, 24)};
        line-height: ${fluid(26, 30)};
        padding: 12px 16px;
        background-color: ${brand.grey.grey91};
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      th {
        ${fonts.agBook.bold};
        font-size: ${fluid(20, 24)};
        line-height: ${fluid(26, 30)};
        background-color: ${brand.grey.grey91};
        padding: 12px 16px;
        text-align: center;
      }

      td {
        ${fonts.agBook.light};
        font-size: ${fluid(18, 22)};
        line-height: ${fluid(24, 30)};
        padding: 12px 16px;
        text-align: center;
      }
    }
  }
`;
const sharedButtonStyles = css `
  ${ButtonStyles.baseButtonStyles};
  ${fonts.agBook.bold}
  font-size: ${fluid(16, 20)};
  color: var(--themeColor);
  --buttonEdgePadding: 22px;
  --buttonBgColor: var(--themeColor);
  --buttonBorderColor: var(--themeColor);
  text-decoration: none;
`;
export const contentStyles = css `
  h1,
  .h1-styles {
    ${h1}
  }

  h2,
  .h2-styles {
    ${h2}
  }

  h3,
  .h3-styles {
    ${h3}
  }

  h4,
  .h4-styles {
    ${h4}
  }

  h5,
  .h5-styles {
    ${h5}
  }

  h6,
  .h6-styles {
    ${h6}
  }

  p {
    ${paragraph}

    a:not(${ButtonStyles.Button}):not([class*='button-style']) {
      ${link};
      color: ${brand.orange};
    }
  }

  .large {
    ${paragraphLarge}
  }

  .small-med {
    ${paragraphSmallMed}
  }

  .small {
    ${paragraphSmall}
  }

  a:not(${ButtonStyles.Button}):not([class*='button-style']) {
    ${link}
    color: ${brand.orange};
  }

  ol,
  ul {
    list-style: none;
    padding-left: 0;
    margin: ${fluid(8, 12)} 0 ${fluid(24, 32)};

    li {
      ${paragraphStyles};
      font-size: inherit;
      line-height: inherit;
      margin: 0 0 ${fluid(16, 24)};
      position: relative;
      padding-left: 40px;

      @media ${from(Device.TabletSmall)} {
        padding-left: 50px;
      }

      p {
        display: inline;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  ol {
    list-style: none;
    counter-reset: counter;

    li {
      counter-increment: counter;

      &::before {
        content: counter(counter) '.';
        position: absolute;
        top: 0;
        left: 0;
        font-size: ${fluid(22, 26)};
        line-height: ${fluid(28, 34)};
      }
    }
  }

  ul {
    li {
      &::before {
        content: '';
        position: absolute;
        top: 5px;
        left: 0;
        width: 10px;
        height: 10px;
        border-radius: 100%;
        background-color: ${brand.black};

        @media ${from(Device.TabletSmall)} {
          left: 10px;
          top: 7px;
        }
      }
    }
  }

  blockquote {
    ${blockquote};

    > strong,
    .quote-name {
      ${blockquoteName}
    }
  }

  em {
    ${fonts.agBook.regularItalic};
  }

  hr {
    display: block;
    background: currentColor;
    border: 0;
    height: 2px;
    margin: ${fluid(32, 48)} 0;
    padding: 0;
    width: 100%;
  }

  table {
    ${table};
  }

  sub {
    vertical-align: sub;
    font-size: smaller;
  }

  .responsive-table {
    position: relative;
    overflow-x: auto;
  }

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  .button-style-cta {
    ${sharedButtonStyles};
    ${ButtonStyles.textButtonStyles};
    --buttonEdgePadding: 0;
    color: ${brand.black};
    padding-right: 36px;
    position: relative;

    &::before,
    &::after {
      content: url(${HoverArrowRight});
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 26px;
      height: 26px;
      margin-left: 10px;
      z-index: 0;
    }

    &::after {
      z-index: 1;
      opacity: 0;
      ${transition('opacity')};
    }

    &:hover {
      opacity: 1;

      &::after {
        content: url(${HoverArrowRightHovered});
        opacity: 1;
      }
    }
  }

  .button-style-solid {
    ${sharedButtonStyles};
    color: ${brand.white};
    --buttonFgColor: ${brand.white};
  }

  .button-style-border {
    ${sharedButtonStyles};
    --buttonBgColor: transparent;
    --buttonFgColor: var(--themeColor);
    --buttonBorderColor: var(--themeColor);
    border: 2px solid var(--buttonBorderColor);
  }
`;
export const tickedBullet = css `
  ul {
    list-style: none;
    padding-top: 0.5rem;
    padding-left: 0;

    li {
      position: relative;
      margin-bottom: ${fluid(16, 20)};
      padding-left: ${fluid(38, 55)};

      &::before {
        content: '';
        position: absolute;
        top: -1px;
        left: 0;
        width: ${fluid(26, 32)};
        height: ${fluid(26, 32)};
        background-image: url(${TickCircle});
        background-size: 100%;
        background-repeat: no-repeat;
        background-position: center;
      }
    }
  }
`;
